import { Button, CreateSearchView, SearchViews } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { ComponentProps, ReactNode } from 'react';
import { LOCAL_VIEW_ID } from './SearchContext';
import {
  useCreateSearchView,
  useDeleteSearchView,
  useSearchViews,
  useUpdateSearchView,
} from './useSearchViewApi';

export function CreateView({
  view,
  onCreate,
  triggerContent,
  triggerVariant = 'default',
  triggerSize = 'default',
  triggerIcon = false,
}: {
  view: Omit<CrewMerchantUi.SearchView, 'id' | 'name'>;
  onCreate: (view: CrewMerchantUi.SearchView) => void;
  triggerContent: ReactNode;
  triggerVariant?: ComponentProps<typeof Button>['variant'];
  triggerSize?: ComponentProps<typeof Button>['size'];
  triggerIcon?: ComponentProps<typeof Button>['icon'];
}) {
  const { mutate: createView, isPending } = useCreateSearchView(onCreate);

  return (
    <CreateSearchView
      onCreate={(values, close) =>
        createView({ ...view, ...values }, { onSuccess: close })
      }
      pending={isPending}
      triggerContent={triggerContent}
      triggerVariant={triggerVariant}
      triggerSize={triggerSize}
      triggerIcon={triggerIcon}
    />
  );
}

type ViewsProps = {
  entity: CrewMerchantUi.SearchView['kind'];
  selectedView: CrewMerchantUi.SearchView;
  onViewSelect: (view: CrewMerchantUi.SearchView | null) => void;
  onViewEdit: (view: CrewMerchantUi.SearchView) => void;
  className?: string;
};

export function Views({
  entity,
  className,
  selectedView,
  onViewEdit,
  onViewSelect,
}: ViewsProps) {
  const { data: views } = useSearchViews(entity);
  const { mutate: updateView, isPending: isUpdatePending } =
    useUpdateSearchView(onViewSelect);
  const { mutate: duplicateView, isPending: isDuplicatePending } =
    useCreateSearchView(onViewSelect);
  const { mutate: deleteView, isPending: isDeletePending } =
    useDeleteSearchView(() => onViewSelect(null));

  return (
    <SearchViews<CrewMerchantUi.SearchView>
      className={className}
      views={views ?? []}
      selectedView={selectedView.id === LOCAL_VIEW_ID ? null : selectedView}
      onViewSelect={onViewSelect}
      onEdit={onViewEdit}
      onRename={(view, close) => updateView(view, { onSuccess: close })}
      onDuplicate={(view, close) => duplicateView(view, { onSuccess: close })}
      onDelete={(view, close) => deleteView(view.id, { onSuccess: close })}
      renamePending={isUpdatePending}
      duplicatePending={isDuplicatePending}
      deletePending={isDeletePending}
    />
  );
}
